import { Image, Link, StyleSheet, Text, View } from "@react-pdf/renderer";
import dayjs from "dayjs";
import React from "react";

import {
  FacilityReportFacilitySnapshotData,
  FacilitySnapshotPost,
  FacilitySnapshotReview,
} from "../../../../../../models/FacilityReportData";
import {
  formatOpeningDate,
  formatSnapshotDate,
  formatStars,
  getAddressLines,
  getHealthScoreDisplay,
  getPostPhotoUrl,
  getPrimaryPhone,
  getRegularHoursByDay,
  SnapshotTranslate,
  SOCIAL_PLATFORMS,
} from "../../../FacilitySnapshotSection/utils";
import { A4_PAGE_HEIGHT, PAGE_PADDING_VERTICAL } from "../styles";

// GBP allows up to 9 sub categories; cap them so the profile card can't
// crowd out the rest of the page
const MAX_SUB_CATEGORIES = 5;

const snapshotColors = {
  text: "#121863",
  muted: "#757799",
  border: "#c9d2df",
  divider: "#e6e9f2",
  background: "#f4f5ff",
  white: "#ffffff",
  link: "#551a8b",
  star: "#f5a623",
};

const HEALTH_SCORE_COLORS: Record<string, string> = {
  good: "#7ac63f",
  warning: "#f69a0e",
  bad: "#dc5b3e",
  critical: "#96001b",
};

const styles = StyleSheet.create({
  container: {
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
    gap: 8,
    color: snapshotColors.text,
  },
  card: {
    backgroundColor: snapshotColors.white,
    borderWidth: 1,
    borderColor: snapshotColors.border,
    borderRadius: 8,
    padding: 10,
  },
  header: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
  },
  facilityName: {
    fontSize: 16,
    fontWeight: 700,
    marginBottom: 2,
  },
  muted: {
    fontSize: 7,
    color: snapshotColors.muted,
  },
  mutedRow: {
    display: "flex",
    flexDirection: "row",
    gap: 10,
  },
  healthIndex: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: 3,
  },
  healthScore: {
    minWidth: 34,
    height: 34,
    paddingHorizontal: 4,
    borderWidth: 1.5,
    borderRadius: 4,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  healthScoreText: {
    fontSize: 16,
    fontWeight: 700,
  },
  body: {
    flex: 1,
    display: "flex",
    gap: 8,
    minHeight: 0,
  },
  profileCard: {
    display: "flex",
    gap: 8,
    overflow: "hidden",
  },
  profileColumn: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    gap: 7,
  },
  field: {
    display: "flex",
    flexDirection: "column",
    gap: 2,
  },
  chip: {
    alignSelf: "flex-start",
    fontSize: 6.5,
    color: snapshotColors.muted,
    borderWidth: 0.75,
    borderColor: snapshotColors.border,
    borderRadius: 6,
    paddingHorizontal: 5,
    paddingVertical: 1,
    marginBottom: 1,
  },
  label: {
    fontSize: 6.5,
    color: snapshotColors.muted,
  },
  value: {
    fontSize: 8,
  },
  valueBold: {
    fontSize: 8,
    fontWeight: 700,
  },
  empty: {
    fontSize: 8,
    color: snapshotColors.muted,
  },
  link: {
    fontSize: 8,
    color: snapshotColors.link,
  },
  divider: {
    borderBottomWidth: 0.75,
    borderBottomColor: snapshotColors.divider,
  },
  hoursGrid: {
    display: "flex",
    flexDirection: "row",
    flexWrap: "wrap",
    rowGap: 2,
  },
  hoursRow: {
    width: "50%",
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    paddingRight: 6,
  },
  hoursText: {
    fontSize: 6.5,
  },
  socialIcons: {
    display: "flex",
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 4,
  },
  socialIcon: {
    width: 15,
    height: 15,
    borderRadius: 7.5,
    borderWidth: 0.75,
    borderColor: snapshotColors.border,
    backgroundColor: snapshotColors.background,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    textDecoration: "none",
  },
  socialIconText: {
    fontSize: 6,
    fontWeight: 700,
    color: snapshotColors.muted,
  },
  main: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    gap: 8,
    minHeight: 0,
  },
  cardHeader: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-end",
    marginBottom: 6,
  },
  cardTitle: {
    fontSize: 10,
    fontWeight: 700,
  },
  itemsRow: {
    display: "flex",
    flexDirection: "row",
    gap: 6,
  },
  itemCard: {
    flex: 1,
    borderWidth: 0.75,
    borderColor: snapshotColors.border,
    borderRadius: 5,
    overflow: "hidden",
  },
  reviewCard: {
    padding: 6,
    display: "flex",
    flexDirection: "column",
    gap: 2,
  },
  reviewHeader: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-end",
    gap: 4,
  },
  reviewName: {
    flex: 1,
    fontSize: 8,
    fontWeight: 700,
  },
  stars: {
    fontSize: 7,
    color: snapshotColors.star,
  },
  reviewComment: {
    fontSize: 6.5,
    color: snapshotColors.muted,
    lineHeight: 1.4,
  },
  postImage: {
    height: 42,
    backgroundColor: snapshotColors.background,
    borderBottomWidth: 0.75,
    borderBottomColor: snapshotColors.border,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  postImagePhoto: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
  },
  postBody: {
    padding: 6,
    display: "flex",
    flexDirection: "column",
    gap: 2,
  },
  postSummary: {
    fontSize: 6.5,
    lineHeight: 1.4,
  },
  description: {
    flex: 1,
    minHeight: 0,
    overflow: "hidden",
  },
  descriptionText: {
    fontSize: 7,
    lineHeight: 1.6,
  },
});

interface IA4FacilitySnapshotSectionProps {
  orientation: "portrait" | "landscape";
  snapshot: FacilityReportFacilitySnapshotData;
  // Post photos pre-loaded as data URLs (index-aligned with
  // snapshot.local_posts.recent_posts); undefined renders the placeholder.
  postImages: (string | undefined)[];
  t: SnapshotTranslate;
}

export const A4FacilitySnapshotSection: React.FunctionComponent<
  IA4FacilitySnapshotSectionProps
> = ({ orientation, snapshot, postImages, t }) => {
  const isPortrait = orientation === "portrait";
  const { reviews, local_posts: localPosts } = snapshot;
  const healthScore = snapshot.health_index
    ? getHealthScoreDisplay(snapshot.health_index)
    : undefined;
  const addressLines = getAddressLines(snapshot, t("postal_prefix"));
  const phone = getPrimaryPhone(snapshot);
  const openingDate = formatOpeningDate(snapshot, t);
  const regularHours = getRegularHoursByDay(snapshot);

  const renderValue = (value: string | undefined) =>
    value ? (
      <Text style={styles.value}>{value}</Text>
    ) : (
      <Text style={styles.empty}>{t("not_registered")}</Text>
    );

  const categories = (
    <>
      {snapshot.main_category && (
        <View style={styles.field}>
          <Text style={styles.chip}>{t("main_category")}</Text>
          <Text style={styles.valueBold}>{snapshot.main_category.name}</Text>
        </View>
      )}
      {snapshot.sub_categories.length > 0 && (
        <View style={styles.field}>
          <Text style={styles.chip}>{t("sub_categories")}</Text>
          {snapshot.sub_categories
            .slice(0, MAX_SUB_CATEGORIES)
            .map((category) => (
              <Text style={styles.value} key={category.gbp_category_id}>
                {category.name}
              </Text>
            ))}
          {snapshot.sub_categories.length > MAX_SUB_CATEGORIES && (
            <Text style={styles.empty}>
              {t("more_sub_categories", {
                count: snapshot.sub_categories.length - MAX_SUB_CATEGORIES,
              })}
            </Text>
          )}
        </View>
      )}
    </>
  );

  const contactFields = (
    <>
      <View style={styles.field}>
        <Text style={styles.label}>{t("address")}</Text>
        {addressLines.length > 0
          ? addressLines.map((line) => (
              <Text style={styles.value} key={line}>
                {line}
              </Text>
            ))
          : renderValue(undefined)}
      </View>
      <View style={styles.field}>
        <Text style={styles.label}>{t("phone")}</Text>
        {renderValue(phone)}
      </View>
      <View style={styles.field}>
        <Text style={styles.label}>{t("website")}</Text>
        {snapshot.website_url ? (
          <Link src={snapshot.website_url} style={styles.link}>
            {snapshot.website_url}
          </Link>
        ) : (
          renderValue(undefined)
        )}
      </View>
      <View style={styles.field}>
        <Text style={styles.label}>{t("opening_date")}</Text>
        {renderValue(openingDate)}
      </View>
    </>
  );

  const hoursAndSocial = (
    <>
      <View style={styles.field}>
        <Text style={styles.label}>{t("regular_hours")}</Text>
        {regularHours ? (
          <View style={styles.hoursGrid}>
            {regularHours.map(({ day, periods }) => (
              <View style={styles.hoursRow} key={day}>
                <Text style={styles.hoursText}>{t(`days.${day}`)}</Text>
                <Text style={styles.hoursText}>
                  {periods.length > 0 ? periods.join(", ") : t("closed")}
                </Text>
              </View>
            ))}
          </View>
        ) : (
          renderValue(undefined)
        )}
      </View>
      <View style={styles.field}>
        <Text style={styles.label}>{t("social_profiles")}</Text>
        <View style={styles.socialIcons}>
          {SOCIAL_PLATFORMS.map(({ key, label, color }) => {
            const url = snapshot.social_profiles?.[key]?.url;
            return url ? (
              <Link
                key={key}
                src={url}
                style={[
                  styles.socialIcon,
                  { backgroundColor: color, borderColor: color },
                ]}
              >
                <Text style={[styles.socialIconText, { color: "#ffffff" }]}>
                  {label}
                </Text>
              </Link>
            ) : (
              <View key={key} style={styles.socialIcon}>
                <Text style={styles.socialIconText}>{label}</Text>
              </View>
            );
          })}
        </View>
      </View>
    </>
  );

  // Landscape mirrors the web layout: a single profile column on the left.
  // Portrait splits the profile into two columns above the main content.
  const profileCard = isPortrait ? (
    <View style={[styles.card, styles.profileCard, { flexDirection: "row" }]}>
      <View style={styles.profileColumn}>
        {categories}
        {contactFields}
      </View>
      <View style={styles.profileColumn}>{hoursAndSocial}</View>
    </View>
  ) : (
    <View
      style={[
        styles.card,
        styles.profileCard,
        { flexDirection: "column", width: 190 },
      ]}
    >
      {categories}
      <View style={styles.divider} />
      {contactFields}
      {hoursAndSocial}
    </View>
  );

  const renderReview = (review: FacilitySnapshotReview, index: number) => (
    <View style={[styles.itemCard, styles.reviewCard]} key={index}>
      <View style={styles.reviewHeader}>
        <Text
          style={[styles.reviewName, { maxLines: 1, textOverflow: "ellipsis" }]}
        >
          {review.reviewer_name || t("no_name")}
        </Text>
        <Text style={styles.muted}>{formatSnapshotDate(review.review_date)}</Text>
      </View>
      <Text style={styles.stars}>{formatStars(review.rating)}</Text>
      <Text
        style={[
          styles.reviewComment,
          { maxLines: isPortrait ? 4 : 3, textOverflow: "ellipsis" },
        ]}
      >
        {review.comment || t("rating_only")}
      </Text>
    </View>
  );

  const renderPost = (post: FacilitySnapshotPost, index: number) => (
    <View style={styles.itemCard} key={index}>
      <View style={styles.postImage}>
        {getPostPhotoUrl(post) && postImages[index] ? (
          <Image src={postImages[index]} style={styles.postImagePhoto} />
        ) : (
          <Text style={styles.muted}>{t("post_image_placeholder")}</Text>
        )}
      </View>
      <View style={styles.postBody}>
        <Text style={styles.muted}>{formatSnapshotDate(post.post_date)}</Text>
        <Text
          style={[styles.postSummary, { maxLines: 3, textOverflow: "ellipsis" }]}
        >
          {post.summary}
        </Text>
      </View>
    </View>
  );

  const reviewsCard = (
    <View style={styles.card}>
      <View style={styles.cardHeader}>
        <Text style={styles.cardTitle}>
          {t("recent_reviews", { count: reviews.recent_reviews.length })}
        </Text>
        {!!reviews.total_review_count && (
          <Text style={styles.muted}>
            {t("review_totals", {
              count: reviews.total_review_count,
              average: (reviews.average_rating ?? 0).toFixed(1),
            })}
          </Text>
        )}
      </View>
      {reviews.recent_reviews.length > 0 ? (
        <View style={styles.itemsRow}>
          {reviews.recent_reviews.map(renderReview)}
        </View>
      ) : (
        <Text style={styles.empty}>{t("no_reviews")}</Text>
      )}
    </View>
  );

  const postsCard = (
    <View style={styles.card}>
      <View style={styles.cardHeader}>
        <Text style={styles.cardTitle}>
          {t("recent_posts", { count: localPosts.recent_posts.length })}
        </Text>
        <Text style={styles.muted}>
          {t("recent_posts_count", {
            days: localPosts.recent_posts_period_days,
            count: localPosts.recent_posts_count,
          })}
        </Text>
      </View>
      {localPosts.recent_posts.length > 0 ? (
        <View style={styles.itemsRow}>
          {localPosts.recent_posts.map(renderPost)}
        </View>
      ) : (
        <Text style={styles.empty}>{t("no_posts")}</Text>
      )}
    </View>
  );

  // Fills whatever height is left; overflowing text is clipped
  const descriptionCard = (
    <View style={[styles.card, styles.description]}>
      <Text style={[styles.cardTitle, { marginBottom: 6 }]}>
        {t("description")}
      </Text>
      {snapshot.profile ? (
        <Text style={styles.descriptionText}>{snapshot.profile}</Text>
      ) : (
        <Text style={styles.empty}>{t("not_registered")}</Text>
      )}
    </View>
  );

  return (
    // The page doesn't wrap, so pin the height to the page's content area;
    // otherwise react-pdf grows the page to fit and nothing gets clipped
    <View
      style={[
        styles.container,
        { height: A4_PAGE_HEIGHT[orientation] - PAGE_PADDING_VERTICAL * 2 },
      ]}
    >
      <View style={[styles.card, styles.header]}>
        <View>
          <Text style={styles.facilityName}>{snapshot.name}</Text>
          <View style={styles.mutedRow}>
            <Text style={styles.muted}>
              {t("facility_id", { id: snapshot.location_id })}
            </Text>
            <Text style={styles.muted}>
              {t("snapshot_date", {
                date: dayjs(snapshot.snapshot_date).format(
                  t("datetime_format")
                ),
              })}
            </Text>
          </View>
        </View>
        {healthScore && (
          <View style={styles.healthIndex}>
            <Text style={styles.muted}>{t("health_index")}</Text>
            <View
              style={[
                styles.healthScore,
                { borderColor: HEALTH_SCORE_COLORS[healthScore.className] },
              ]}
            >
              <Text
                style={[
                  styles.healthScoreText,
                  { color: HEALTH_SCORE_COLORS[healthScore.className] },
                ]}
              >
                {healthScore.label}
              </Text>
            </View>
          </View>
        )}
      </View>
      {isPortrait ? (
        <View style={[styles.body, { flexDirection: "column" }]}>
          {profileCard}
          {reviewsCard}
          {postsCard}
          {descriptionCard}
        </View>
      ) : (
        <View style={[styles.body, { flexDirection: "row" }]}>
          {profileCard}
          <View style={styles.main}>
            {reviewsCard}
            {postsCard}
            {descriptionCard}
          </View>
        </View>
      )}
    </View>
  );
};

/**
 * react-pdf only supports PNG/JPEG and fetches images itself, which Google
 * may block. Pre-load post photos so a failure falls back to the placeholder
 * instead of breaking the PDF.
 */
export const loadSnapshotPostImages = async (
  snapshot: FacilityReportFacilitySnapshotData
): Promise<(string | undefined)[]> =>
  Promise.all(
    snapshot.local_posts.recent_posts.map(async (post) => {
      const url = getPostPhotoUrl(post);
      if (!url) return undefined;
      try {
        const response = await fetch(url, { referrerPolicy: "no-referrer" });
        if (!response.ok) return undefined;
        const blob = await response.blob();
        if (!["image/png", "image/jpeg"].includes(blob.type)) return undefined;
        return await new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result as string);
          reader.onerror = reject;
          reader.readAsDataURL(blob);
        });
      } catch {
        return undefined;
      }
    })
  );
